import React, { useContext } from 'react';
import { BSFLLogo, ETBLogo, HyfiLogo, TGPLogo, YOULogo } from '../components/CurriculumLogos';
import CurriculumContext from '../contexts/CurriculumContext';
import { AgeCategory, BrandCode, CurriculumBrand } from '../features/curriculum';
import { logger } from '../utils/logger';

declare global {
  interface Window {
    featureToggles: {
      get: (feature: string) => boolean;
    };
  }
}

// Factory method for creating a curriculum object.
// Should be used instead of useCurriculum when not using CurriculumProvider.
export const createCurriculum: (
  brandCode: BrandCode,
  ageCategory?: AgeCategory
) => { brand: CurriculumBrand; ageCategory?: AgeCategory } | null = (
  brandCode: BrandCode,
  ageCategory?: AgeCategory
) => {
  if (!brandCode) return null;

  const code = brandCode?.toUpperCase() as BrandCode;
  const brand = brands[code];

  if (!brand) {
    logger.warn(`Invalid curriculum brand code: ${code}`);
    return null;
  }

  return {
    brand,
    ageCategory,
  };
};

export const useCurriculum = () => {
  const curriculum = useContext(CurriculumContext);

  if (curriculum === undefined) {
    throw new Error('useCurriculum must be used within a CurriculumProvider');
  }

  if (curriculum === null) {
    throw new Error('Invalid curriculum context');
  }

  return curriculum;
};

export default useCurriculum;

export const brands: Record<BrandCode, CurriculumBrand> = {
  BSFL: {
    code: 'BSFL',
    name: 'Bible Studies for Life',
    description: 'Intentional, biblical discipleship rooted in everyday life',
    logo: <BSFLLogo />,
    imgSrc: 'img/BSFL.png',
    background: 'linear-gradient(180deg, #f05a47 0%, #ff8979 100%)',
  },
  ETB: {
    code: 'ETB',
    name: 'Explore the Bible',
    description: 'An expository, book-by-book \nstudy of Scripture',
    logo: <ETBLogo fill="#111111" />,
    imgSrc: 'img/ETB.png',
    background: 'linear-gradient(180deg, #CECECE 0%, #EAEAEA 100%)',
  },
  TGP: {
    code: 'TGP',
    name: 'The Gospel Project',
    description: 'A Christ-centered, chronological study of Scripture',
    logo: <TGPLogo />,
    imgSrc: 'img/TGP_v2.png',
    background: 'linear-gradient(180deg, #1F2638 0%, #6375A5 100%)',
  },
  YOU: {
    code: 'YOU',
    name: 'You',
    description: 'A Bible Study for Urban and Multicultural Believers',
    logo: <YOULogo />,
    imgSrc: 'img/YOU.png',
    background: 'linear-gradient(69.95deg, #EBF3F9 4.99%, #B0DDFC 69.32%)',
  },
  HYFI: {
    code: 'HYFI',
    name: 'Hyfi',
    description: 'Digital Curriculum for the \nNext Generation',
    logo: <HyfiLogo />,
    imgSrc: 'img/hyfi.png',
    background: 'center / cover url("img/hyfi-background.jpg") no-repeat',
  },
  VBS: {
    code: 'VBS',
    name: 'Vacation Bible School',
    description: 'VBS 2026: Shining a Light on Who Jesus Really Is',
    imgSrc: 'https://assets.lifeway.com/f_auto/GRID_VBS_PREVIEW_01_IlluminationStation_FullColor',
    background: '#23429c',
  },
  HYFICHRISTMAS: {
    code: 'HYFICHRISTMAS',
    name: 'Hyfi Christmas Preview',
    description: 'Not a Hyfi subscriber? Preview Hyfi with sessions from Christmas 2024',
    logo: <HyfiLogo />,
    imgSrc: 'img/hyfi.png',
    background: 'top center / cover url("img/hyfi-christmas-preview/bible-study-card-bg.jpg") no-repeat',
  },
};
